import {getFieldTypeOcticon, toIssueFieldType} from '@github-ui/issue-fields-shared/issue-field'
import {
  IssueFieldMultiSelectValueTokens,
  IssueFieldSingleSelectValueToken,
} from '@github-ui/issue-metadata/IssueFieldSingleSelectValueToken'
import {Link} from '@primer/react'

import {createIssueEventExternalUrl} from '../utils/urls'
import type {IssueFieldDataType} from './__generated__/IssueFieldAddedEvent.graphql'
import {IssueEventRationale} from './IssueEventRationale'
import styles from './IssueFieldEvent.module.css'
import type {TimelineRowEventActor$key} from './row/__generated__/TimelineRowEventActor.graphql'
import {TimelineRow} from './row/TimelineRow'

// Helper function to parse dates from the backend API
// The backend returns ISO 8601 timestamps (e.g., "2026-01-14T00:00:00.000Z")
// but we need to preserve the date portion regardless of the user's timezone.
function stripTimezone(isoString: string | null): Date | null {
  if (!isoString) return null

  // Validate ISO 8601 date format (YYYY-MM-DD at the start)
  const dateMatch = isoString.match(/^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})/)
  if (!dateMatch?.groups) return null

  const {year, month, day} = dateMatch.groups
  if (!year || !month || !day) return null

  const yearNum = parseInt(year, 10)
  const monthNum = parseInt(month, 10)
  const dayNum = parseInt(day, 10)

  // Validate numeric ranges
  if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) return null
  if (monthNum < 1 || monthNum > 12) return null
  if (dayNum < 1 || dayNum > 31) return null

  // Create a Date at noon UTC to avoid timezone issues
  // This ensures the date displays correctly in any timezone
  const date = new Date(Date.UTC(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0))

  // Final validation: check if Date construction succeeded
  return isNaN(date.getTime()) ? null : date
}

type IssueFieldValueOption = {
  readonly name: string
  readonly color: string | null | undefined
}

export function normalizeMultiSelectOption(name: string, color: string | null | undefined) {
  return {name, color: color ?? ''}
}

export function normalizeMultiSelectOptions(options: readonly IssueFieldValueOption[] | null | undefined) {
  return (options ?? []).map(option => normalizeMultiSelectOption(option.name, option.color))
}

export function parseFallbackMultiSelectOptions(
  newValue: string | null | undefined,
  newColor: string | null | undefined,
) {
  const names = (newValue ?? '').split(',')
  const colors = (newColor ?? '').split(',')
  const colorsMatch = names.length === colors.length

  return names
    .map((name, i) => normalizeMultiSelectOption(name.trim(), colorsMatch ? (colors[i]?.trim() ?? '') : ''))
    .filter(option => option.name)
}

export function getValue(
  dataType: string,
  newValue: string | undefined,
  newColor: string | null | undefined,
  options?: readonly IssueFieldValueOption[] | null,
) {
  // MULTI_SELECT fields can have no newValue but still have options, and vice versa, so we handle both cases here.
  if (dataType === 'MULTI_SELECT') {
    if (!newValue && (!options || options.length === 0)) {
      return null
    } else {
      const normalizedOptions = options?.length
        ? normalizeMultiSelectOptions(options)
        : parseFallbackMultiSelectOptions(newValue, newColor)

      return (
        <IssueFieldMultiSelectValueTokens
          options={normalizedOptions}
          getTooltipText={() => undefined}
          containerClassName={styles.inlineMultiContainer}
        />
      )
    }
  }
  // The rest of the issue field types are expected to have newValue.
  if (!newValue) {
    return null
  }
  if (dataType === 'SINGLE_SELECT') {
    return <IssueFieldSingleSelectValueToken name={newValue} color={newColor || ''} getTooltipText={() => undefined} />
  } else if (dataType === 'DATE') {
    // Parse the date and format it in UTC to show consistent calendar date
    const date = stripTimezone(newValue)
    if (!date) return 'Invalid Date'
    return date.toLocaleDateString(undefined, {year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC'})
  }
  return newValue
}

export function getSearchLink(
  repositoryNameWithOwner: string | undefined,
  fieldName: string,
  fieldValue: string | undefined,
): string | undefined {
  if (!repositoryNameWithOwner || !fieldValue) {
    return undefined
  }

  const sanitizedFieldName = fieldName.replaceAll(' ', '-')
  const query = encodeURIComponent(`field.${sanitizedFieldName}:"${fieldValue}"`)
  return `/${repositoryNameWithOwner}/issues?q=${query}`
}

export type IssueFieldEventBaseProps = {
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string | null | undefined
  refAttribute?: React.RefObject<HTMLLIElement | null>
  repositoryNameWithOwner?: string
}

type IssueFieldEventProps = IssueFieldEventBaseProps & {
  fieldValue?: string | null | undefined
  fieldOptions?: readonly IssueFieldValueOption[] | null | undefined
  dataType?: IssueFieldDataType
  fieldName?: string
  color?: string | null | undefined
  databaseId?: number | null | undefined
  actor: TimelineRowEventActor$key | null | undefined
  createdAt: string
  trailingLabel?: string
  leadingLabel?: string
  hasValue: boolean
  rollupContent?: React.ReactNode
  rationale?: string
  analyticsAction?: string
}

export function IssueFieldEvent({
  fieldValue,
  fieldOptions,
  dataType,
  fieldName,
  color,
  databaseId,
  actor,
  highlightedEventId,
  repositoryNameWithOwner,
  refAttribute,
  createdAt,
  issueUrl,
  onLinkClick,
  trailingLabel,
  leadingLabel,
  hasValue,
  rollupContent,
  rationale,
  analyticsAction,
}: IssueFieldEventProps) {
  if (rollupContent) {
    const highlighted = String(databaseId) === highlightedEventId

    return (
      <TimelineRow
        highlighted={highlighted}
        refAttribute={refAttribute}
        actor={actor}
        createdAt={createdAt}
        deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
        onLinkClick={onLinkClick}
        leadingIcon={getFieldTypeOcticon(toIssueFieldType(dataType || 'TEXT'))}
      >
        <TimelineRow.Main>{rollupContent}</TimelineRow.Main>
      </TimelineRow>
    )
  }

  const hasDisplayValue = Boolean(fieldValue) || Boolean(fieldOptions?.length)

  if ((hasValue && !hasDisplayValue) || !dataType || !fieldName) {
    return null
  }

  const highlighted = String(databaseId) === highlightedEventId

  const value = hasValue ? getValue(dataType, fieldValue || undefined, color, fieldOptions) : undefined
  const searchValue =
    fieldValue || (fieldOptions?.length ? fieldOptions.map(option => option.name).join(', ') : undefined)
  const searchLink = getSearchLink(repositoryNameWithOwner, fieldName, searchValue)

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={getFieldTypeOcticon(toIssueFieldType(dataType))}
    >
      <TimelineRow.Main>
        {leadingLabel}
        <div className={styles.issueFieldTokenWrapper}>{fieldName}</div>
        {trailingLabel}
        {value && (
          <div className={styles.issueFieldTokenWrapper}>
            <Link href={searchLink} inline className={styles.issueFieldValueLink}>
              {value}
            </Link>
          </div>
        )}
        {rationale && (
          <IssueEventRationale
            rationale={rationale}
            issueEventDatabaseId={String(databaseId)}
            analyticsAction={analyticsAction ?? ''}
          />
        )}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
